import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Skeleton from "@mui/material/Skeleton";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SettingsIcon from "@mui/icons-material/Settings";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import SpeedIcon from "@mui/icons-material/Speed";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../api";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "./PromoCars.style.css";
import { VEHICLE_TYPES } from "../../constants/vehicleTypes";

const BASE = process.env.REACT_APP_API_BASE_URL;
const SELLABLE_TYPES = VEHICLE_TYPES.filter(v => v.enabled);

const PromoCars = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cars, setCars]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [transmissions, setTransmissions] = useState([]);
  const [fuelTypes, setFuelTypes]     = useState([]);
  const [brands, setBrands]           = useState([]);
  const [models, setModels]           = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  /* ── Favorites ─────────────────────────── */
  useEffect(() => {
    if (localStorage.getItem("isLoggedIn") !== "true") return;
    api.get("/favorites/favorites")
      .then(res => {
        const ids = new Set(
          (Array.isArray(res.data) ? res.data : []).map(f => f.carId)
        );
        setFavoriteIds(ids);
      })
      .catch(() => {});
  }, []);

  const toggleFav = useCallback(async (carId) => {
    if (localStorage.getItem("isLoggedIn") !== "true") { localStorage.setItem("redirectAfterLogin", window.location.pathname); navigate("/Login"); return; }
    const isFav = favoriteIds.has(carId);
    setFavoriteIds(prev => {
      const next = new Set(prev);
      isFav ? next.delete(carId) : next.add(carId);
      return next;
    });
    try {
      isFav ? await api.delete(`/favorites/${carId}`) : await api.post(`/favorites/${carId}`);
    } catch {
      setFavoriteIds(prev => {
        const next = new Set(prev);
        isFav ? next.add(carId) : next.delete(carId);
        return next;
      });
    }
  }, [favoriteIds, navigate]);

  /* ── Data fetching ──────────────────────── */
  useEffect(() => {
    axios.get(`${BASE}/api/cars/fuel-types`).then(r => setFuelTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${BASE}/api/cars/transmissions`).then(r => setTransmissions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${BASE}/api/cars/brands`).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${BASE}/api/cars/models`).then(r => setModels(r.data)).catch(() => {});
  }, []);

  /* Chaque type de véhicule a sa propre annonce/table côté backend — on
     récupère les trois "promo" et on les fusionne, tagués avec leur type pour
     router correctement le lien de détail et savoir quelles specs afficher. */
  useEffect(() => {
    Promise.allSettled(
      SELLABLE_TYPES.map(vt => axios.get(`${BASE}/api/${vt.apiSegment}/promo`))
    ).then(results => {
      const merged = results.flatMap((res, i) => {
        if (res.status !== 'fulfilled') return [];
        const vt = SELLABLE_TYPES[i];
        return (Array.isArray(res.value.data) ? res.value.data : []).map(item => ({
          ...item,
          vehicleType: vt.key,
          detailPath: vt.detailPath,
        }));
      });
      setCars(merged);
    }).finally(() => setLoading(false));
  }, []);

  /* ── Helpers ────────────────────────────── */
  const getFuel         = id => fuelTypes.find(f => f.id === id)?.name      || "—";
  const getTransmission = id => transmissions.find(t => t.id === id)?.name  || "—";
  const getBrand        = car => car.vehicleType === 'cars'
    ? (brands.find(b => b.makeId === car.brandId)?.makeName || "—")
    : (car.brandName || "—");
  const getModel        = car => car.vehicleType === 'cars'
    ? (models.find(m => m.model_id === car.modelId)?.model_name || "—")
    : (car.modelName || "—");

  if (!loading && cars.length === 0) return null;

  return (
    <section className="promo">
      <div className="promo__inner">

        {/* Header */}
        <div className="promo__head">
          <div className="promo__head-left">
            <span className="promo__label">
              <LocalOfferIcon sx={{ fontSize: 15 }} />
              {t('promoLabel')}
            </span>
            <h2 className="promo__title">{t('promoCarsTitle')}</h2>
          </div>
          <Link to="/featured-cars" className="promo__see-all">
            {t('seeAllListings')} <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Link>
        </div>

        {/* Carousel */}
        <Swiper
          className="promo-swiper"
          modules={[Navigation]}
          navigation
          rewind
          spaceBetween={20}
          breakpoints={{
            0:    { slidesPerView: 1 },
            600:  { slidesPerView: 2 },
            900:  { slidesPerView: 3 },
            1200: { slidesPerView: 4 },
          }}
        >
          {loading
            ? [...Array(4)].map((_, i) => (
              <SwiperSlide key={i}>
                <div className="promo-card--skeleton">
                  <Skeleton variant="rectangular" width="100%" height={195} />
                  <div style={{ padding: 16 }}>
                    <Skeleton width="70%" height={20} style={{ marginBottom: 8 }} />
                    <Skeleton width="45%" height={28} style={{ marginBottom: 10 }} />
                    <Skeleton width="90%" height={16} />
                  </div>
                </div>
              </SwiperSlide>
            ))
            : cars.map(car => {
              const isFav = favoriteIds.has(car.id);
              const isCarType = car.vehicleType === 'cars';
              return (
                <SwiperSlide key={`${car.vehicleType}-${car.id}`}>
                  <Link to={`/${car.detailPath}/${car.id}`} className="promo-card">

                    {/* Image */}
                    <div className="promo-card__img-wrap">
                      <img
                        src={`${BASE}/car-images/${car.image}`}
                        alt={`${getBrand(car)} ${getModel(car)}`}
                        loading="lazy"
                        onError={e => { e.target.src = "/placeholder-car.png"; }}
                      />
                      <span className="promo-card__badge">{t("promotionBadge")}</span>
                      {car.status === 'Vendu' && (
                        <div className="promo-card__sold-ribbon">{t("soldBadge")}</div>
                      )}
                      {isCarType && (
                        <button
                          className={`promo-card__heart${isFav ? " promo-card__heart--active" : ""}`}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); toggleFav(car.id); }}
                          aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
                        >
                          {isFav
                            ? <FavoriteIcon sx={{ fontSize: 17, color: "#e53935" }} />
                            : <FavoriteBorderIcon sx={{ fontSize: 17, color: "#666" }} />
                          }
                        </button>
                      )}
                    </div>

                    {/* Body */}
                    <div className="promo-card__body">
                      <h3 className="promo-card__title">
                        {getBrand(car)} {getModel(car)} {car.generationName || ''} {car.yearOfManufacture || ''}
                      </h3>

                      <p className="promo-card__price">
                        {car.price ? `${car.price.toLocaleString()} ${t("currency")}` : t("priceNotSpecified")}
                      </p>

                      {/* Spec pills */}
                      <div className="promo-card__specs">
                        <span className="promo-card__spec">
                          <CalendarTodayIcon /> {car.yearOfManufacture}
                        </span>
                        <span className="promo-card__spec">
                          <SpeedIcon /> {car.mileage?.toLocaleString()} {t("km")}
                        </span>
                        {isCarType && (
                          <>
                            <span className="promo-card__spec">
                              <LocalGasStationIcon /> {t(`fuelTypes.${getFuel(car.fuelTypeId)}`)}
                            </span>
                            <span className="promo-card__spec">
                              <SettingsIcon /> {t(`transmissions.${getTransmission(car.transmissionId)}`)}
                            </span>
                          </>
                        )}
                      </div>

                      <span className="promo-card__cta">
                        {t('promoCardCta')} <ArrowForwardIcon sx={{ fontSize: 14 }} />
                      </span>
                    </div>

                  </Link>
                </SwiperSlide>
              );
            })
          }
        </Swiper>

      </div>
    </section>
  );
};

export default PromoCars;
